import { dateLocale } from "@/lib/dateTime";
import React, { useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, ArrowUpDown, Inbox } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn, formatINR } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Meaning colours for reports: green good / growth / done, red decline / overdue, blue information,
 * orange pending or a warning, purple comparisons and special metrics, gray neutral.
 */
export type Tone = "blue" | "green" | "red" | "orange" | "purple" | "gray";
export const TONE: Record<Tone, { chip: string; text: string; bar: string; soft: string }> = {
  blue: { chip: "bg-info-soft text-info", text: "text-info", bar: "bg-info", soft: "bg-info-soft" },
  green: { chip: "bg-success-soft text-success", text: "text-success", bar: "bg-success", soft: "bg-success-soft" },
  red: { chip: "bg-danger-soft text-danger", text: "text-danger", bar: "bg-danger", soft: "bg-danger-soft" },
  orange: { chip: "bg-warning-soft text-warning", text: "text-warning", bar: "bg-warning", soft: "bg-warning-soft" },
  purple: { chip: "bg-purple-soft text-purple", text: "text-purple", bar: "bg-purple", soft: "bg-purple-soft" },
  gray: { chip: "bg-muted text-muted-foreground", text: "text-muted-foreground", bar: "bg-muted-foreground/50", soft: "bg-muted" },
};

/** Tone for a % of target: met = green, under half = red, otherwise orange */
export const achievementTone = (pct: number | null | undefined): Tone => (pct === null || pct === undefined ? "gray" : pct >= 100 ? "green" : pct < 50 ? "red" : "orange");

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** ₹ amount, compacted to K / L / Cr for tiles and chart labels */
export function fmtMoney(value: number, compact = true): string {
  const v = Number(value) || 0;
  if (!compact) return formatINR(v);
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(abs >= 1e8 ? 0 : 1)}Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(abs >= 1e6 ? 0 : 1)}L`;
  if (abs >= 1e3) return `₹${(v / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
  return formatINR(Math.round(v));
}

export const fmtPct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(v % 1 === 0 ? 0 : 1)}%`);
export const fmtNum = (v: number) => (Number(v) || 0).toLocaleString("en-IN");

export function fmtDate(v?: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtMonth(period: string): string {
  const [y = 1970, m = 1] = period.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(dateLocale(), { month: "short" });
}

/** Trigger a browser download of CSV text */
export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------

export function SectionCard({
  title,
  description,
  action,
  children,
  className,
  icon,
  tone = "blue",
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** A small coloured icon before the title */
  icon?: React.ReactNode;
  tone?: Tone;
}) {
  return (
    <Card className={cn("shadow-xs gap-4 py-5", className)}>
      <CardHeader className="px-5 flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1 min-w-0">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            {icon && <span className={cn("flex size-6 shrink-0 items-center justify-center rounded-md [&_svg]:size-3.5", TONE[tone].chip)}>{icon}</span>}
            {title}
          </CardTitle>
          {description && <CardDescription className="text-xs">{description}</CardDescription>}
        </div>
        {action}
      </CardHeader>
      <CardContent className="px-5">{children}</CardContent>
    </Card>
  );
}

export function EmptyState({ message = "No data for this period." }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-xs text-muted-foreground">
      <Inbox className="size-5" />
      <span>{message}</span>
    </div>
  );
}

/** KPI tile: headline value with an optional comparison against the previous period */
export function StatTile({
  label,
  value,
  previous,
  previousLabel = "vs last month",
  hint,
  icon,
  format = "number",
  rawValue,
  tone = "blue",
  lowerIsBetter,
}: {
  label: string;
  value: string;
  /** Previous-period raw value; pass with rawValue to show a change */
  previous?: number;
  previousLabel?: string;
  rawValue?: number;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  format?: "number" | "pct";
  /** Accent colour by meaning */
  tone?: Tone;
  /** A rise is bad news (e.g. overdue items): colour the change the other way */
  lowerIsBetter?: boolean;
}) {
  let delta: React.ReactNode = null;
  if (previous !== undefined && rawValue !== undefined) {
    const diff = rawValue - previous;
    const up = diff >= 0;
    const text =
      format === "pct"
        ? `${up ? "+" : ""}${diff.toFixed(1)} pts`
        : previous === 0
          ? rawValue === 0 ? "no change" : "new"
          : `${up ? "+" : ""}${((diff / Math.abs(previous)) * 100).toFixed(0)}%`;
    const good = diff === 0 ? null : lowerIsBetter ? !up : up;
    delta = (
      <span className={cn("inline-flex items-center gap-0.5 font-semibold", good === null ? "text-foreground" : good ? "text-success" : "text-danger")}>
        {up ? <ArrowUpRight className="size-3.5" aria-hidden /> : <ArrowDownRight className="size-3.5" aria-hidden />}
        {text}
      </span>
    );
  }

  return (
    <Card className="relative gap-2 overflow-hidden py-4 shadow-xs">
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-1", TONE[tone].bar)} />
      <CardHeader className="px-4 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</CardTitle>
        {icon && <div className={cn("flex size-8 items-center justify-center rounded-lg [&_svg]:size-4", TONE[tone].chip)}>{icon}</div>}
      </CardHeader>
      <CardContent className="px-4 space-y-1">
        <div className="text-2xl font-bold tabular-nums text-foreground">{value}</div>
        {(delta || hint) && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {delta}
            {delta && <span>{previousLabel}</span>}
            {hint}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Placeholder shapes while a report loads for the first time */
export function ReportSkeleton({ tiles = 4, cards = 2 }: { tiles?: number; cards?: number }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading report">
      <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
        {Array.from({ length: tiles }, (_, i) => (
          <div key={i} className="space-y-2.5 rounded-xl border border-border bg-card p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-28" />
            <Skeleton className="h-3 w-32" />
          </div>
        ))}
      </div>
      <div className="grid gap-3 @4xl/main:grid-cols-2">
        {Array.from({ length: cards }, (_, i) => (
          <div key={i} className="space-y-3 rounded-xl border border-border bg-card p-5">
            <Skeleton className="h-4 w-40" />
            {Array.from({ length: 5 }, (_, j) => (
              <Skeleton key={j} className="h-4" style={{ width: `${90 - j * 12}%` }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A small coloured label for a status, always with words */
export function StatusPill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return <span className={cn("inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-md px-2 text-[11.5px] font-semibold", TONE[tone].chip)}>{children}</span>;
}

/** Progress against a target, with the % in words; colour follows achievementTone */
export function ProgressBar({ pct, className }: { pct: number | null; className?: string }) {
  if (pct === null) return <span className="text-xs text-muted-foreground">No target</span>;
  const t = TONE[achievementTone(pct)];
  return (
    <span className={cn("flex items-center gap-2", className)}>
      <span className="h-1.5 flex-1 overflow-hidden rounded-full" style={{ background: "var(--viz-track)" }}>
        <span className={cn("block h-full rounded-full", t.bar)} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </span>
      <span className={cn("w-11 text-right font-mono text-xs font-semibold", t.text)}>{Math.round(pct)}%</span>
    </span>
  );
}

/** Target progress: filled bar plus the achieved % as text (never color alone) */
export function TargetBar({ achieved, target, compact }: { achieved: number; target: number; compact?: boolean }) {
  if (!target) {
    return <span className="text-xs text-muted-foreground">No target set</span>;
  }
  const pctValue = (achieved / target) * 100;
  return (
    <div className={cn("space-y-1 w-full", compact ? "min-w-[120px]" : "min-w-0")}>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--viz-track)" }}>
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.min(100, pctValue)}%`, background: "var(--viz-series-1)" }}
        />
      </div>
      <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
        <span className="font-semibold text-foreground pr-2">{pctValue.toFixed(0)}%</span>
        <span>
          {fmtMoney(achieved)} / {fmtMoney(target)}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Charts (single series → one hue, no legend; values labelled, tooltip on hover/focus)
// ---------------------------------------------------------------------------

interface ChartTooltip {
  x: number;
  y: number;
  title: string;
  lines: string[];
}

function TooltipBox({ tip }: { tip: ChartTooltip | null }) {
  if (!tip) return null;
  return (
    <div
      role="status"
      className="pointer-events-none absolute z-20 rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md -translate-x-1/2 -translate-y-full"
      style={{ left: tip.x, top: tip.y - 8 }}
    >
      {tip.lines.map((l, i) => (
        <div key={i} className={i === 0 ? "font-semibold text-foreground tabular-nums" : "text-muted-foreground tabular-nums"}>
          {l}
        </div>
      ))}
      <div className="text-muted-foreground">{tip.title}</div>
    </div>
  );
}

export interface BarDatum {
  label: string;
  value: number;
  /** Extra tooltip lines, e.g. "12 deals" */
  detail?: string[];
}

/** Horizontal bar list: label left, bar from a shared baseline, value at the tip */
export function BarList({
  data,
  format = fmtNum,
  emptyMessage,
  tone,
}: {
  data: BarDatum[];
  format?: (v: number) => string;
  emptyMessage?: string;
  /** Bar colour by meaning (default: the chart blue) */
  tone?: Tone;
}) {
  const [tip, setTip] = useState<ChartTooltip | null>(null);
  const max = Math.max(0, ...data.map((d) => d.value));
  if (data.length === 0 || max === 0) return <EmptyState message={emptyMessage} />;

  const show = (e: React.SyntheticEvent<HTMLElement>, d: BarDatum) => {
    const el = e.currentTarget;
    const parent = el.closest("[data-chart-root]") as HTMLElement;
    const r = el.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();
    setTip({ x: r.left - pr.left + r.width / 2, y: r.top - pr.top, title: d.label, lines: [format(d.value), ...(d.detail || [])] });
  };

  return (
    <div className="relative space-y-2" data-chart-root onMouseLeave={() => setTip(null)}>
      {data.map((d) => (
        <div
          key={d.label}
          tabIndex={0}
          className="group grid grid-cols-[minmax(80px,30%)_1fr] items-center gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring py-0.5"
          onMouseEnter={(e) => show(e, d)}
          onFocus={(e) => show(e, d)}
          onBlur={() => setTip(null)}
          aria-label={`${d.label}: ${format(d.value)}`}
        >
          <span className="truncate text-xs text-muted-foreground" title={d.label}>
            {d.label}
          </span>
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <div
                className={cn("h-4 rounded-r-[4px] transition-opacity group-hover:opacity-80", tone && TONE[tone].bar)}
                style={{ width: `${Math.max(1.5, (d.value / max) * 100)}%`, ...(tone ? {} : { background: "var(--viz-series-1)" }) }}
              />
            </div>
            <span className="text-xs font-semibold tabular-nums text-foreground w-16 text-right shrink-0">{format(d.value)}</span>
          </div>
        </div>
      ))}
      <TooltipBox tip={tip} />
    </div>
  );
}

/** Vertical columns over time (e.g. monthly revenue); labels the latest and the peak column */
export function ColumnChart({
  data,
  format = fmtNum,
  height = 160,
  emptyMessage,
}: {
  data: BarDatum[];
  format?: (v: number) => string;
  height?: number;
  emptyMessage?: string;
}) {
  const [tip, setTip] = useState<ChartTooltip | null>(null);
  const max = Math.max(0, ...data.map((d) => d.value));
  if (data.length === 0 || max === 0) return <EmptyState message={emptyMessage} />;
  const peak = data.findIndex((d) => d.value === max);
  const last = data.length - 1;

  const show = (e: React.SyntheticEvent<HTMLElement>, d: BarDatum) => {
    const el = e.currentTarget;
    const parent = el.closest("[data-chart-root]") as HTMLElement;
    const r = el.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();
    const bar = el.querySelector("[data-bar]") as HTMLElement | null;
    const top = bar ? bar.getBoundingClientRect().top : r.top;
    setTip({ x: r.left - pr.left + r.width / 2, y: top - pr.top, title: d.label, lines: [format(d.value), ...(d.detail || [])] });
  };

  return (
    <div className="relative" data-chart-root onMouseLeave={() => setTip(null)}>
      <div className="flex items-end gap-[2px] border-b border-border" style={{ height }}>
        {data.map((d, i) => {
          const h = (d.value / max) * (height - 20);
          const labelled = i === peak || i === last;
          return (
            <div
              key={d.label + i}
              tabIndex={0}
              className="group relative flex-1 h-full flex flex-col items-center justify-end outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
              onMouseEnter={(e) => show(e, d)}
              onFocus={(e) => show(e, d)}
              onBlur={() => setTip(null)}
              aria-label={`${d.label}: ${format(d.value)}`}
            >
              {labelled && d.value > 0 && (
                <span className="text-[10px] font-semibold tabular-nums text-foreground mb-0.5 whitespace-nowrap">{format(d.value)}</span>
              )}
              <div
                data-bar
                className="w-full max-w-[24px] rounded-t-[4px] transition-colors group-hover:[background:var(--viz-series-1-hover)]"
                style={{ height: Math.max(d.value > 0 ? 2 : 0, h), background: "var(--viz-series-1)" }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-[2px] mt-1.5">
        {data.map((d, i) => (
          <span key={d.label + i} className="flex-1 overflow-visible whitespace-nowrap text-center text-[10px] text-muted-foreground">
            {/* With many columns, label every few so the labels stay readable */}
            {data.length <= 14 || i % Math.ceil(data.length / 10) === 0 || i === last ? d.label : ""}
          </span>
        ))}
      </div>
      <TooltipBox tip={tip} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable data table
// ---------------------------------------------------------------------------

export interface Column<T> {
  key: string;
  header: string;
  /** Value used for sorting; defaults to row[key] */
  sortValue?: (row: T) => number | string;
  render?: (row: T) => React.ReactNode;
  align?: "left" | "right";
  className?: string;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyMessage,
  defaultSort,
  footer,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  defaultSort?: { key: string; dir: "asc" | "desc" };
  footer?: React.ReactNode;
}) {
  const [sort, setSort] = useState(defaultSort || null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const value = (r: T) => (col?.sortValue ? col.sortValue(r) : r[sort.key]);
    return [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va ?? "").localeCompare(String(vb ?? ""));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  if (rows.length === 0) return <EmptyState message={emptyMessage} />;

  return (
    <div className="overflow-x-auto -mx-5 px-5">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn("py-2 px-2 font-semibold whitespace-nowrap", c.align === "right" ? "text-right" : "text-left", c.className)}
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() =>
                    setSort((s) => ({ key: c.key, dir: s?.key === c.key && s.dir === "desc" ? "asc" : "desc" }))
                  }
                >
                  {c.header}
                  <ArrowUpDown className={cn("size-3", sort?.key === c.key ? "opacity-100" : "opacity-30")} aria-hidden />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={rowKey(r)}
              className={cn("border-b border-border/60 last:border-0", onRowClick && "cursor-pointer hover:bg-accent/60")}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn("py-2 px-2 whitespace-nowrap tabular-nums", c.align === "right" ? "text-right" : "text-left", c.className)}
                >
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer}
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Split bar: one row's total split into coloured parts (e.g. leads by idle time)
// ---------------------------------------------------------------------------

export interface StackPart {
  label: string;
  value: number;
  tone: Tone;
}

/** The colour key for split bars; every part is also named in the bar's label and tooltip */
export function StackLegend({ parts }: { parts: { label: string; tone: Tone }[] }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {parts.map((p) => (
        <span key={p.label} className="inline-flex items-center gap-1.5">
          <span aria-hidden className={cn("size-2.5 rounded-sm", TONE[p.tone].bar)} />
          {p.label}
        </span>
      ))}
    </div>
  );
}

/** A labelled row whose bar is split into parts; the bar length is the row's share of the largest row */
export function StackedBar({ label, parts, max, total, onClick }: { label: React.ReactNode; parts: StackPart[]; max: number; total?: number; onClick?: () => void }) {
  const sum = total ?? parts.reduce((t, p) => t + p.value, 0);
  const words = parts.filter((p) => p.value > 0).map((p) => `${p.label}: ${fmtNum(p.value)}`).join(", ");
  const Row = onClick ? "button" : "div";
  return (
    <Row
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn("grid w-full grid-cols-[minmax(80px,28%)_1fr_48px] items-center gap-3 rounded-md py-0.5 text-left text-xs", onClick && "hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}
      title={words || "None"}
    >
      <span className="truncate text-foreground/80">{label}</span>
      <span className="flex h-3.5 overflow-hidden rounded-sm" style={{ background: "var(--viz-track)" }} role="img" aria-label={`${typeof label === "string" ? label : ""} ${words || "none"}`}>
        <span className="flex h-full" style={{ width: `${max > 0 ? (sum / max) * 100 : 0}%` }}>
          {parts.map((p) => (p.value > 0 ? <span key={p.label} className={cn("h-full", TONE[p.tone].bar)} style={{ width: `${(p.value / Math.max(1, sum)) * 100}%` }} /> : null))}
        </span>
      </span>
      <span className="text-right font-mono font-semibold tabular-nums">{fmtNum(sum)}</span>
    </Row>
  );
}
